import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate, Link } from "react-router-dom";
import { API, useAuth } from "@/AuthContext";
import { useI18n } from "@/i18n";
import { toast } from "sonner";

export default function GiftClaim() {
  const { token } = useParams();
  const { user, loading } = useAuth();
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const [gift, setGift] = useState(null);
  const [err, setErr] = useState(null);
  const [claiming, setClaiming] = useState(false);

  useEffect(() => {
    axios.get(`${API}/gifts/${token}`).then(r => setGift(r.data)).catch(e => setErr(e?.response?.data?.detail || "Gift not found"));
  }, [token]);

  const signIn = () => {
    const redirectUrl = window.location.origin + `/gifts/claim/${token}`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const claim = async () => {
    try {
      setClaiming(true);
      const res = await axios.post(`${API}/gifts/${token}/claim`);
      toast.success(t("gift_claimed_success"));
      if (res.data.kind === "capital" && res.data.city) {
        nav(`/recipes?city=${encodeURIComponent(res.data.city)}`);
      } else {
        nav("/dashboard");
      }
    } catch (e) {
      setClaiming(false);
      toast.error(e?.response?.data?.detail || "Claim failed");
    }
  };

  if (err) {
    return (
      <main className="max-w-2xl mx-auto px-6 py-32 text-center" data-testid="gift-error">
        <div className="overline">Gift</div>
        <h1 className="font-serif text-4xl mt-3">{err}</h1>
        <Link to="/" className="btn-ghost mt-8 inline-flex">{t("nav_portfolio")}</Link>
      </main>
    );
  }
  if (!gift) return <main className="max-w-2xl mx-auto px-6 py-32 text-center text-zinc-500" data-testid="gift-loading">…</main>;

  const sender = gift.sender_name || "A friend";
  return (
    <main className="max-w-3xl mx-auto px-6 py-20" data-testid="gift-claim-page">
      <div className="glass relative overflow-hidden grain p-10 lg:p-14">
        <div className="overline">{t("gift_for_you")}</div>
        <h1 className="font-serif text-5xl tracking-tighter mt-4 leading-[1.0]">
          {gift.recipient_name ? `${t("hello")} ${gift.recipient_name},` : `${t("hello")}.`}
        </h1>
        <p className="font-serif italic gold text-3xl mt-2">
          {sender} {t("gift_has_gifted_you")} {t("gift_the")} {gift.label}.
        </p>

        {gift.personal_message && (
          <div className="mt-8 border-l-2 border-[#D4AF37]/50 pl-5 py-3 text-zinc-300 italic font-serif text-lg leading-relaxed" data-testid="gift-message">
            "{gift.personal_message}"
          </div>
        )}

        <p className="text-zinc-400 mt-8 max-w-xl">
          {t("gift_explainer")}
        </p>

        <div className="mt-10 flex items-baseline gap-3">
          <div className="font-serif text-5xl gold">£{gift.amount}</div>
          <div className="overline">{t("gift_value")}</div>
        </div>

        <div className="mt-10">
          {gift.claimed ? (
            <div data-testid="gift-already-claimed">
              <div className="tag tag-gold">{t("gift_already_claimed")} ✓</div>
              <div className="mt-4">
                <Link to="/dashboard" className="btn-gold">{t("nav_dashboard")} →</Link>
              </div>
            </div>
          ) : !user && !loading ? (
            <>
              <p className="text-zinc-300 mb-4 text-sm">{t("gift_sign_in_to_claim")}</p>
              <button onClick={signIn} className="btn-gold" data-testid="gift-signin">
                {t("sign_in_with_google")} →
              </button>
            </>
          ) : user ? (
            <>
              {user.email?.toLowerCase() !== gift.recipient_email?.toLowerCase() && (
                <p className="text-xs text-zinc-500 mb-3" data-testid="gift-email-mismatch">
                  {t("gift_email_mismatch")} ({gift.recipient_email})
                </p>
              )}
              <button onClick={claim} disabled={claiming} className="btn-gold" data-testid="gift-claim-btn">
                {claiming ? "…" : t("gift_claim_cta")} →
              </button>
            </>
          ) : null}
        </div>

        <p className="overline mt-12 text-zinc-600">{t("gift_lifetime_note")}</p>
      </div>
    </main>
  );
}
